import axios from 'axios';

let installed = false;
let isRefreshing = false;
// Queue of callbacks waiting for an in-flight refresh to finish. Each is
// resolved with the new token (or null if the refresh failed) so concurrent
// 401s share a single refresh instead of racing or logging out.
let refreshWaiters = [];

function resolveWaiters(token) {
  refreshWaiters.forEach((cb) => cb(token));
  refreshWaiters = [];
}

async function performRefresh() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;
  try {
    // Bare axios call (no interceptor recursion) to refresh the session.
    const { data } = await axios.post('/api/auth/refresh', { refreshToken }, { __authRetry: true });
    if (data?.token) {
      localStorage.setItem('token', data.token);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      return data.token;
    }
    return null;
  } catch {
    return null;
  }
}

function logout() {
  // Clear the token AND the per-user cached identity so nothing leaks to the
  // next login (name, role, store).
  ['token', 'refreshToken', 'user', 'user_name', 'user_role', 'storeInfo', 'adminActingStore']
    .forEach((k) => localStorage.removeItem(k));
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/admin/login';
  }
}

export function installAuthInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config || {};
      const status = error.response?.status;
      const message = error.response?.data?.message || '';

      // Only act on auth-related 401s, and never on the refresh call itself.
      const isAuthError = status === 401 && /token|auth|profile/i.test(message);
      if (!isAuthError || originalRequest.__authRetry) {
        return Promise.reject(error);
      }

      // A request that was sent WITHOUT an Authorization header is a client-side
      // race, not an expired session — don't tear down the session for it.
      const sentAuthHeader = !!(originalRequest.headers && (
        originalRequest.headers.Authorization || originalRequest.headers.authorization
      ));
      if (!sentAuthHeader) {
        return Promise.reject(error);
      }

      // If a refresh is already running, wait for it instead of starting another.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshWaiters.push((newToken) => {
            if (!newToken) return reject(error);
            originalRequest.__authRetry = true;
            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${newToken}`,
            };
            resolve(axios(originalRequest));
          });
        });
      }

      isRefreshing = true;
      const newToken = await performRefresh();
      isRefreshing = false;
      resolveWaiters(newToken);

      if (!newToken) {
        // Refresh genuinely failed → the session is unrecoverable.
        logout();
        return Promise.reject(error);
      }

      originalRequest.__authRetry = true;
      originalRequest.headers = {
        ...(originalRequest.headers || {}),
        Authorization: `Bearer ${newToken}`,
      };
      return axios(originalRequest);
    }
  );
}
